import React, { useState, useEffect } from 'react';
import { ParkingType, ParkingTypeFormData } from '../types/parking';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface ParkingTypeFormSliderProps {
  workspaceId: string;
  typeId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
}

const PARKING_IMAGES = [
  { id: 'black-single', name: 'Black Single', image: '/parking-icons/black-single-car.png' },
  { id: 'black-tandem', name: 'Black Tandem', image: '/parking-icons/black-tandam-cars.png' },
  { id: 'blue-single', name: 'Blue Single', image: '/parking-icons/blue-single-car.png' },
  { id: 'blue-tandem', name: 'Blue Tandem', image: '/parking-icons/blue-tandam-cars.png' },
  { id: 'light-blue-single', name: 'Light Blue Single', image: '/parking-icons/light-blue-single-car.png' },
  { id: 'light-blue-tandem', name: 'Light Blue Tandem', image: '/parking-icons/light-blue-tandam-cars.png' },
  { id: 'mint-single', name: 'Mint Single', image: '/parking-icons/mint-single-car.png' },
  { id: 'mint-tandem', name: 'Mint Tandem', image: '/parking-icons/mint-tandam-cars.png' },
  { id: 'green-single', name: 'Green Single', image: '/parking-icons/green-single-car.png' },
  { id: 'green-tandem', name: 'Green Tandem', image: '/parking-icons/green-tandam-cars.png' },
  { id: 'orange-single', name: 'Orange Single', image: '/parking-icons/orange-single-car.png' },
  { id: 'orange-tandem', name: 'Orange Tandem', image: '/parking-icons/orange-tandam-cars.png' },
  { id: 'pink-single', name: 'Pink Single', image: '/parking-icons/pink-single-car.png' },
  { id: 'pink-tandem', name: 'Pink Tandem', image: '/parking-icons/pink-tandam-cars.png' },
  { id: 'purple-single', name: 'Purple Single', image: '/parking-icons/purple-single-car.png' },
  { id: 'purple-tandem', name: 'Purple Tandem', image: '/parking-icons/purple-tandam-cars.png' },
  { id: 'red-single', name: 'Red Single', image: '/parking-icons/red-single-car.png' },
  { id: 'red-tandem', name: 'Red Tandem', image: '/parking-icons/red-tandam-cars.png' },
  { id: 'yellow-single', name: 'Yellow Single', image: '/parking-icons/yellow-single-car.png' },
  { id: 'yellow-tandem', name: 'Yellow Tandem', image: '/parking-icons/yellow-tandam-cars.png' },
];

const ParkingTypeFormSlider: React.FC<ParkingTypeFormSliderProps> = ({
  workspaceId,
  typeId,
  onSuccess,
  onCancel
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [initialProjectId, setInitialProjectId] = useState('');
  const [standardImage, setStandardImage] = useState('/parking-icons/light-blue-single-car.png'); // Default standard image
  const [selectedImage, setSelectedImage] = useState('/parking-icons/red-single-car.png'); // Default selected image

  const initialFormData: ParkingTypeFormData = {
    projectId: initialProjectId,
    name: '',
    total: '0',
    available: '0',
    marketingHold: '0',
    salesHold: '0',
    sold: '0',
    customerSelection: '0',
    customerSubmitted: '0'
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project if creating new type
      if (!typeId && projectsList.length > 0) {
        setInitialProjectId(projectsList[0]._id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  const validateForm = (data: ParkingTypeFormData): string | null => {
    if (!data.projectId) {
      return 'Project selection is required';
    }
    if (!data.name.trim()) {
      return 'Parking type name is required';
    }
    return null;
  };

  const transformFetchData = (response: any): ParkingTypeFormData => {
    const type: ParkingType = response.parkingType;

    // Set images from fetched data
    if (type.standardImage) {
      setStandardImage(type.standardImage);
    }
    if (type.selectedImage) {
      setSelectedImage(type.selectedImage);
    }

    return {
      projectId: type.projectId || '',
      name: type.name || '',
      total: type.total?.toString() || '0',
      available: type.available?.toString() || '0',
      marketingHold: type.marketingHold?.toString() || '0',
      salesHold: type.salesHold?.toString() || '0',
      sold: type.sold?.toString() || '0',
      customerSelection: type.customerSelection?.toString() || '0',
      customerSubmitted: type.customerSubmitted?.toString() || '0'
    };
  };

  const transformSubmitData = (data: ParkingTypeFormData) => {
    return {
      projectId: data.projectId,
      name: data.name.trim(),
      standardImage: standardImage,
      selectedImage: selectedImage,
      total: data.total ? parseInt(data.total) : 0,
      available: data.available ? parseInt(data.available) : 0,
      marketingHold: data.marketingHold ? parseInt(data.marketingHold) : 0,
      salesHold: data.salesHold ? parseInt(data.salesHold) : 0,
      sold: data.sold ? parseInt(data.sold) : 0,
      customerSelection: data.customerSelection ? parseInt(data.customerSelection) : 0,
      customerSubmitted: data.customerSubmitted ? parseInt(data.customerSubmitted) : 0
    };
  };

  if (!typeId && loadingProjects) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={initialProjectId}
      workspaceId={workspaceId}
      itemId={typeId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/parking-types`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="parking-type-form"
    >
      {({ formData, handleInputChange, loading }) => (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Project */}
            <div className="form-group">
              <label htmlFor="projectId" className="form-label">
                Project
              </label>
              {loadingProjects ? (
                <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <select
                  id="projectId"
                  name="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                >
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Standard Image */}
          <div className="form-group">
            <label className="form-label">
              Standard image:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginTop: '10px' }}>
              {PARKING_IMAGES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setStandardImage(item.image)}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F4F6',
                    border: standardImage === item.image ? '3px solid #4CAF50' : '2px solid #D1D5DB',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px'
                  }}
                  disabled={loading}
                  title={item.name}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  {standardImage === item.image && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Image */}
          <div className="form-group">
            <label className="form-label">
              Selected image:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginTop: '10px' }}>
              {PARKING_IMAGES.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedImage(item.image)}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F4F6',
                    border: selectedImage === item.image ? '3px solid #4CAF50' : '2px solid #D1D5DB',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px'
                  }}
                  disabled={loading}
                  title={item.name}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  {selectedImage === item.image && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default ParkingTypeFormSlider;
